import { useCallback, useState } from "react";
import { ExerciseAssortLimits } from "../../../models/ExerciseAssortLimits";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../utils/hooks/useFacility";
import { GET_EXERCISE_ASSORT_LIMITS } from "../../../api/apiRoutes";
import getId from "../../../../utils/getId";

export default function useTourLimits() {
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const [limits, setLimits] = useState<ExerciseAssortLimits[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFallback, setIsFallback] = useState<boolean>(true); // true dopóki nie pobierzemy limitów

  const fetchLimits = useCallback(
    async (exerciseIds: string) => {
      setIsLoading(true);

      try {
        const { result, isError } = await get(GET_EXERCISE_ASSORT_LIMITS(facilityIds, exerciseIds));

        if (!isError && result && Array.isArray(result) && result.length > 0) {
          setLimits(result);
          setIsFallback(false);
        } else {
          setLimits([]);
          setIsFallback(true);
        }
      } catch {
        setLimits([]);
        setIsFallback(true);
      } finally {
        setIsLoading(false);
      }
    },
    [facilityIds, get],
  );

  const getLimitForAssort = (assortId: string, exerciseGroupId: string): number | null => {
    // Jeśli fallback, zwracamy null - SingleConcert użyje starej logiki
    if (isFallback || limits.length === 0) {
      return null;
    }

    // Normalizuj format ID (zawsze "db,id")
    const normalizeId = (id: string) => {
      if (!id) return "";
      // Jeśli zawiera przecinek, jest już znormalizowane
      if (id.includes(",")) return id;
      // W przeciwnym razie to może być pojedyncze ID
      return id;
    };

    const normalizedAssortId = normalizeId(assortId);
    const normalizedExerciseGroupId = normalizeId(exerciseGroupId);

    // Znajdź limit dla tego asortymentu i grupy zajęciowej
    const limit = limits.find((limit) => {
      const limitAssortId = getId(limit.assortIds);
      const limitExerciseGroupId = getId(limit.exerciseGroupIds);

      return limitAssortId === normalizedAssortId && limitExerciseGroupId === normalizedExerciseGroupId;
    });

    if (limit) {
      const { maxQuantity, remainingQuantity } = limit;

      // maxQuantity === 0 lub null -> brak limitu (korzystamy tylko z pojemności wydarzenia)
      if (maxQuantity === null || maxQuantity === 0) {
        return null;
      }

      // Dla bezpieczeństwa, przy istnieniu limitu traktujemy brak remainingQuantity jak 0 (wyprzedane)
      if (remainingQuantity === null || remainingQuantity === undefined) {
        return 0;
      }

      // 0 = wyprzedane (limit osiągnięty)
      // Liczba > 0 = pozostało tyle biletów
      return remainingQuantity;
    }

    // Nie znaleziono limitu dla tego biletu - zwracamy null (brak limitu)
    return null;
  };

  return {
    limits,
    isLoading,
    isFallback,
    getLimitForAssort,
    fetchLimits,
  };
}
